import _ from 'lodash'
import * as THREE from 'three'

import Rectangle from '@/model/rectangle'
import GlassSegment from '@/model/glassSegment'
import MathsUtils from '@/utils/mathsUtils'
import OptimiserUtils from '@/apps/glass-production-station/src/classes/optimiserUtils'

import { SheetAllocationTypes, OrderItemEdgeworkTypes, OrderEditorCutoutTypes } from '@/store/types'
import { SegmentTypes } from '@/store/mathTypes'

/**
 * Optimiser Allocation in the sheet
 * Represents an entry linked to the sheet & a node in the sheet
 */
export default class OptimiserAllocation {
  constructor(data) {
    this.id = data.id;
    this.barcode = data.barcode;
    this.type = data.type;
    this.status = data.status;
    this.cuttingIndex = data.cuttingIndex || 0;
    this.isRotated = !!data.isRotated;
    this.isUserLocked = !!data.isUserLocked;
    this.packingNodeId = data.packingNodeId || 0;

    this.x = data.x;
    this.y = data.y;
    this.width = data.width;
    this.height = data.height;
    this.isPanelSizeOverridden = !!data.isPanelSizeOverridden;
    this.isPanelShapeCuttingDisabled = !!data.isPanelShapeCuttingDisabled;
    this.isPanelTrimOverridden = !!data.isPanelTrimOverridden;
    this.isPanelTemplateHandled = !!data.isPanelTemplateHandled;
    this.isPanelCutoutsRequireHandling = !!data.isPanelCutoutsRequireHandling;
    this.isPanelCutoutsHandled = !!data.isPanelCutoutsHandled;

    this.sourceWidth = data.sourceWidth;
    this.sourceHeight = data.sourceHeight;
    this.angle = data.angle || 0;

    this.flippedHorz = !!data.flippedHorz;
    this.flippedVert = !!data.flippedVert;

    this.trimTop = data.trimTop || 0;
    this.trimRight = data.trimRight || 0;
    this.trimBottom = data.trimBottom || 0;
    this.trimLeft = data.trimLeft || 0;
    this.panelTrimData = _.cloneDeep(data.panelTrimData || []);

    this.overflowTop = data.overflowTop || 0;
    this.overflowRight = data.overflowRight || 0;
    this.overflowBottom = data.overflowBottom || 0;
    this.overflowLeft = data.overflowLeft || 0;

    this.userInfo = data.userInfo || '';
    this.blankPanelUUID = data.blankPanelUUID || '';

    this.optimiserSheetId = data.optimiserSheetId || null;
    this.productionPanelId = data.productionPanelId || null;
    this.glassLevelId = data.glassLevelId || null;
    this.orderItemId = data.orderItemId || null;
    this.orderId = data.orderId || null;

    this.productionPanel = _.cloneDeep(data.productionPanel || {});
    this.glassLevel = _.cloneDeep(data.glassLevel || {});
    this.orderItem = _.cloneDeep(data.orderItem || {});
    this.order = _.cloneDeep(data.order || {});

    this.reliefCutList = (data.reliefCutList || []).map(reliefCut => {
      return new GlassSegment(SegmentTypes.line, {
        startPosition: reliefCut.startPosition,
        endPosition: reliefCut.endPosition
      })
    })
    this.cutoutList = (data.cutoutList || []).map(cutout => {
      return {
        index: cutout.index
      }
    })

    // Editor settings
    this.selected = false;
    this.highlighted = false;
  }


  /**
   * Clone this allocation object
   */
  clone() {
    let newAlloc = new OptimiserAllocation(this)

    return newAlloc
  }


  /**
   * Set the panel size override info
   */
  setSizeOverridden(sourceWidth, sourceHeight) {  
    this.isPanelSizeOverridden = true
    this.sourceWidth = sourceWidth
    this.sourceHeight = sourceHeight

    this.recalculateSize()
   
    // Force the allocation to be locked
    this.isUserLocked = true
  }


  /**
   * Restore the allocation to the original size
   * Typically done after disabling 'panel size override'
   */
  restoreSize() {
    this.isPanelSizeOverridden = false

    this.sourceWidth = this.productionPanel.panelWidth
    this.sourceHeight = this.productionPanel.panelHeight
    
    this.recalculateSize()
  }


  /**
   * Set the panel trim override info
   */
  setTrimOverridden(trimTop, trimRight, trimBottom, trimLeft) {  
    this.isPanelTrimOverridden = true
    this.trimTop = trimTop
    this.trimRight = trimRight
    this.trimBottom = trimBottom
    this.trimLeft = trimLeft

    // Reset back to original size
    this.sourceWidth = this.productionPanel.panelWidth
    this.sourceHeight = this.productionPanel.panelHeight

    this.recalculateSize()
   
    // Force the allocation to be locked
    this.isUserLocked = true
  }


  /**
   * Restore the allocation trim data to the original trim
   * Typically done after disabling 'panel trim override'
   */
  restoreTrim() {
    this.isPanelTrimOverridden = false

    let trimData = this.panelTrimData
    this.trimTop = trimData[0] || 0
    this.trimRight = trimData[1] || 0
    this.trimBottom = trimData[2] || 0
    this.trimLeft = trimData[3] || 0
    
    this.recalculateSize()
  }


  /**
   * Update the default panel trim data set by the server
   */
  updateTrimData(trimTop, trimRight, trimBottom, trimLeft) {
    this.panelTrimData = [trimTop, trimRight, trimBottom, trimLeft]

    if(!this.isPanelTrimOverridden)
      this.restoreTrim()

    this.recalculateSize()
  }


  /**
   * Recalculate the size info based on source size info & trim data
   */
  recalculateSize() {
    let rotated = this.angle == 90 || this.angle == 270

    // Transform trims
    let trimInfo = OptimiserUtils.calculateTransformedTrimValues(
      this.trimTop,
      this.trimRight,
      this.trimBottom,
      this.trimLeft,
      this.angle,
      this.flippedHorz,
      this.flippedVert
    )

    // Clear trim if size overridden / shape cuts disabled
    if(this.isPanelSizeOverridden || this.isPanelShapeCuttingDisabled) {
      trimInfo.trimTop = 0
      trimInfo.trimRight = 0
      trimInfo.trimBottom = 0
      trimInfo.trimLeft = 0
    }

    this.width = rotated ? this.sourceHeight : this.sourceWidth
    this.height = rotated ? this.sourceWidth : this.sourceHeight

    this.width += trimInfo.trimLeft + trimInfo.trimRight
    this.height += trimInfo.trimTop + trimInfo.trimBottom
  }


  /**
   * Clear all relief cuts from the allocation
   */
  removeAllReliefCuts() {
    this.reliefCutList = []
  }


  /**
   * Add a relief cut to the list for the allocation
   */
  addReliefCut(startPosition, endPosition) {
    this.reliefCutList.push({
      startPosition: startPosition,
      endPosition: endPosition
    })
  }


  /**
   * Add a cutout to the allocation to be cut at optimisation
   */
  addCutout(cutoutIndex) {
    // Make sure it isn't already in the allocation's cutout list
    var existingCutout = this.cutoutList.find(cutoutInfo => cutoutInfo.index == cutoutIndex)
    if(!existingCutout)
      this.cutoutList.push({ index: cutoutIndex })
  }


  /**
   * Check if a cutout is in the cutout list flagged for cutting at optimisation
   */
  isCutoutFlagged(cutoutIndex) {
    return this.cutoutList.some(cutoutInfo => cutoutInfo.index == cutoutIndex)
  }


  /**
   * Remove a cutout from the allocation so it's no longer cut at optimisation
   */
  removeCutout(cutoutIndex) {
    this.cutoutList = this.cutoutList.filter(cutoutInfo => cutoutInfo.index != cutoutIndex)
  }


  /**
   * Remove all cutouts from the allocation so cutouts are no longer cut at optimisation
   */
  removeAllCutouts() {
    this.cutoutList = []
  }



  /**
   * Calculate the transformed trim values based on rotation/flip
   */
  generateTransformTrimValues() {
    return OptimiserUtils.calculateTransformedTrimValues(
      this.trimTop,
      this.trimRight,
      this.trimBottom,
      this.trimLeft,
      this.angle,
      this.flippedHorz,
      this.flippedVert
    )
  }


  /**
   * Generate the list of relief cuts transformed into sheet position/rotation/flip
   * Returned as an array of GlassSegments
   */
  generateTransformedReliefCuts() {
    let offset = new THREE.Vector2(-this.width/2, -this.height/2)
    if(this.angle != 0 && this.angle != 180)
      offset.set(-this.height/2, -this.width/2)

    let trimOffset = new THREE.Vector2(this.trimLeft, this.trimTop)

    let mid = new THREE.Vector2(this.x + this.width/2, this.y + this.height/2)
    let allocOrigin = new THREE.Vector2(0, 0)
    let rads = MathsUtils.degreesToRadians(this.angle)
    let scale = new THREE.Vector2(this.flippedHorz ? -1 : 1, this.flippedVert ? -1 : 1)

    return this.reliefCutList.map(reliefCut => {
      let startPos = new THREE.Vector2(reliefCut.startPosition.x, reliefCut.startPosition.y)
      let endPos = new THREE.Vector2(reliefCut.endPosition.x, reliefCut.endPosition.y)

      startPos.add(trimOffset)
      endPos.add(trimOffset)
      startPos.add(offset)
      endPos.add(offset)

      startPos = MathsUtils.rotate2DPoint(allocOrigin, startPos, rads)
      endPos = MathsUtils.rotate2DPoint(allocOrigin, endPos, rads)

      startPos.multiply(scale)
      startPos.add(mid)
      endPos.multiply(scale)
      endPos.add(mid)

      // Clamp to allocation bounds
      startPos.x = MathsUtils.clampValue(startPos.x, this.x, this.x + this.width)
      startPos.y = MathsUtils.clampValue(startPos.y, this.y, this.y + this.height)
      endPos.x = MathsUtils.clampValue(endPos.x, this.x, this.x + this.width)
      endPos.y = MathsUtils.clampValue(endPos.y, this.y, this.y + this.height)

      return new GlassSegment(SegmentTypes.line, {
        startPosition: startPos,
        endPosition: endPos
      })
    })
  }


  /**
   * Check if a point is inside the allocation
   */
  isPointInside(x, y) {
    return (
      this.getBoundsLeft() <= x && this.getBoundsRight() >= x && 
      this.getBoundsTop() <= y && this.getBoundsBottom() >= y
    )
  }


  /**
   * Check if a rectangle intersects with this allocation rectangle
   * Returns true if overlapping or intersection occurs
   */
  isRectangleIntersection(x, y, width, height) {
    // Check if rectangle completely inside
    if(this.getBoundsLeft() <= x && (x+width) <= this.getBoundsRight() && this.getBoundsTop() <= y && (y+height) <= this.getBoundsBottom())
      return true

    // Check intersection
    return !((x+width) <= this.getBoundsLeft() || x >= this.getBoundsRight() || (y+height) <= this.getBoundsTop() || y >= this.getBoundsBottom());
  }


  /**
   * Check if this allocation is a panel
   */
  isPanel() {
    return this.type == SheetAllocationTypes.panel || this.type == SheetAllocationTypes.panelScratchPad
  }


  /**
   * Get the rectangle bounds of the allocation
   */
  getRectangle() {
    return new Rectangle(
      this.getBoundsLeft(),
      this.getBoundsTop(),
      this.getBoundsRight() - this.getBoundsLeft(),
      this.getBoundsBottom() - this.getBoundsTop()
    )
  }


  /**
   * Get the bounds of the allocation
   * Accounts for overflow with offcuts
   */
  getBoundsTop() {
    if(this.type == SheetAllocationTypes.offcut)
      return this.y - this.overflowTop
    return this.y
  }
  getBoundsRight() {
    if(this.type == SheetAllocationTypes.offcut)
      return this.x + this.width + this.overflowRight
    return this.x + this.width
  }
  getBoundsBottom() {
    if(this.type == SheetAllocationTypes.offcut)
      return this.y + this.height + this.overflowBottom
    return this.y + this.height
  }
  getBoundsLeft() {
    if(this.type == SheetAllocationTypes.offcut)
      return this.x - this.overflowLeft
    return this.x
  }
  getBoundsWidth() {
    return this.getBoundsRight() - this.getBoundsLeft()
  }
  getBoundsHeight() {
    return this.getBoundsBottom() - this.getBoundsTop()
  }


  /**
   * Get the panel's shapeData path
   * For non-order editor generated panels, returns path that makes the rectangle
   * 
   * Returned as an array of GlassSegments
   */
  getShapeDataPanelPath() {
    if(!this.orderItem || !this.orderItem.shapeData)
      return []

    // Simple rectangle
    if(!this.orderItem.isEditorGeneratedItem || !this.orderItem.shapeData) {
      var panelWidth = this.productionPanel.panelWidth
      var panelHeight = this.productionPanel.panelHeight

      return [
        new GlassSegment(SegmentTypes.line, { startPosition: { x: 0, y: 0 }, endPosition: { x: panelWidth, y: 0 }}),
        new GlassSegment(SegmentTypes.line, { startPosition: { x: panelWidth, y: 0 }, endPosition: { x: panelWidth, y: panelHeight }}),
        new GlassSegment(SegmentTypes.line, { startPosition: { x: panelWidth, y: panelHeight }, endPosition: { x: 0, y: panelHeight }}),
        new GlassSegment(SegmentTypes.line, { startPosition: { x: 0, y: panelHeight }, endPosition: { x: 0, y: 0 }})
      ]
    }

    // Panel shape path
    return (this.orderItem.shapeData.path || []).map(segmentData => {
      return new GlassSegment(segmentData.type, segmentData)
    })
  }


  /**
   * Generate the outline path for the panel allocation transformed into sheet position/rotation/flip
   */
  generateTransformedPath() {
    if(!this.orderItem || !this.orderItem.shapeData)
      return []

    return this.transformSegmentList(this.orderItem.shapeData.path || [])    
  }


  /**
   * Generate the holes list for the panel allocation transformed into sheet position/rotation/flip
   */
  generateTransformedHoles() {
    if(!this.orderItem || !this.orderItem.shapeData)
      return []

    let holes = this.orderItem.shapeData.holes || []
    return holes.map(hole => {
      return {
        path: this.transformSegmentList(hole.path || [])
      }
    })
  }


  /**
   * Generate the cutting list for the panel allocation transformed into the sheet position/rotation/flip
   * Returns an array of segments (with additional edgework info) for each of the cuts
   */
  generateTransformedCuttingList() {
    if(!this.orderItem)
      return []

    // Ignore if size overidden or cutting disabled
    if(this.isPanelSizeOverridden || this.isPanelShapeCuttingDisabled)
      return []

    var panelWidth = this.productionPanel.panelWidth
    var panelHeight = this.productionPanel.panelHeight

    // If not an order generated item, but has custom trim, add the edges of the panel to the cutting list
    if(!this.orderItem.isEditorGeneratedItem && this.isPanelTrimOverridden) {
      if(this.trimTop > 0 || this.trimRight > 0 || this.trimBottom > 0 || this.trimLeft > 0) {

        var trimSegmentList = []
        if(this.trimTop > 0) {
          trimSegmentList.push(new GlassSegment(SegmentTypes.line, {
            startPosition: { x: 0, y: 0 },
            endPosition: { x: panelWidth, y: 0 }
          }))
        }
        if(this.trimRight > 0) {
          trimSegmentList.push(new GlassSegment(SegmentTypes.line, {
            startPosition: { x: panelWidth, y: 0 },
            endPosition: { x: panelWidth, y: panelHeight }
          }))
        }
        if(this.trimBottom > 0) {
          trimSegmentList.push(new GlassSegment(SegmentTypes.line, {
            startPosition: { x: 0, y: panelHeight },
            endPosition: { x: panelWidth, y: panelHeight }
          }))
        }
        if(this.trimLeft > 0) {
          trimSegmentList.push(new GlassSegment(SegmentTypes.line, {
            startPosition: { x: 0, y: 0 },
            endPosition: { x: 0, y: panelHeight }
          }))
        }

        return this.transformSegmentList(trimSegmentList)
      }
    }


    if(this.orderItem.isEditorGeneratedItem) {
      var cuttingEdgeworkList = this.orderItem.shapeData.cuttingEdgeworkList || []

      var cuttingList = cuttingEdgeworkList.filter(cuttingSegment => {
        // Always add cutting segments
        if(cuttingSegment.isCuttingSegment)
          return true

        // Add boundary cutting segments if there is trim between the segment & the edge
        // Otherwise the bounds rectangle will handle the cutting
        if(cuttingSegment.isBoundarySegment) {
          let startPos = new THREE.Vector2(cuttingSegment.startPosition.x, cuttingSegment.startPosition.y)
          let endPos = new THREE.Vector2(cuttingSegment.endPosition.x, cuttingSegment.endPosition.y)
          let segDir = endPos.clone().sub(startPos)
          segDir.normalize()
          let segMid = new THREE.Vector2((endPos.x + startPos.x) / 2, (endPos.y + startPos.y) / 2)

          // Find matching trim side
          if(Math.abs(segDir.x) > Math.abs(segDir.y)) {
            if(segMid.y < panelHeight/2 && this.trimTop > 0)
              return true
            else if(segMid.y > panelHeight/2 && this.trimBottom > 0)
              return true
          } else {
            if(segMid.x < panelWidth/2 && this.trimLeft > 0)
              return true
            else if(segMid.x > panelWidth/2 && this.trimRight > 0)
              return true
          }
        }
        return false
      })


      return this.transformSegmentList(cuttingList)
    }

    return []
  }


  /**
   * Generate the edgework details for the panel allocation transformed into sheet position/rotation/flip
   * Returns an array of edgework info, containing fields for colour & segments
   */
  generateTransformEdgeworks() {
    if(!this.orderItem)
      return []

    if(this.orderItem.isEditorGeneratedItem) {

      // Edgework is containing in shapeData for the order generated items
      let cuttingEdgeworkList = this.orderItem.shapeData.cuttingEdgeworkList || []
      return _.compact(cuttingEdgeworkList.map(cuttingSegment => {
        if(!cuttingSegment.hasEdgework)
          return null

        let edgeworkSegments = this.transformSegmentList([cuttingSegment])

        return {
          colour: cuttingSegment.edgeworkColour,
          segments: edgeworkSegments
        }
      }))

    } else {
      // Standard line-item
      let edgeworkDetailList = this.orderItem.edgeworkDetailsList || []
      let isPanelLongHorizontal = this.orderItem.glassWidth >= this.orderItem.glassHeight

      return edgeworkDetailList.map(edgeworkData => {

        let startPosition = new THREE.Vector2(0, 0)
        let endPosition = new THREE.Vector2(0, 0)
        switch(edgeworkData.type) {
          case OrderItemEdgeworkTypes.longA:
            if(isPanelLongHorizontal) {
              startPosition.set(0, 0)
              endPosition.set(this.sourceWidth, 0)
            } else {
              startPosition.set(0, 0)
              endPosition.set(0, this.sourceHeight)
            }
            break

          case OrderItemEdgeworkTypes.longB:
            if(isPanelLongHorizontal) {
              startPosition.set(0, this.sourceHeight)
              endPosition.set(this.sourceWidth, this.sourceHeight)
            } else {
              startPosition.set(this.sourceWidth, 0)
              endPosition.set(this.sourceWidth, this.sourceHeight)
            }
            break

          case OrderItemEdgeworkTypes.shortA:
            if(isPanelLongHorizontal) {
              startPosition.set(0, 0)
              endPosition.set(0, this.sourceHeight)
            } else {
              startPosition.set(0, 0)
              endPosition.set(this.sourceWidth, 0)
            }
            break

          case OrderItemEdgeworkTypes.shortB:
            if(isPanelLongHorizontal) {
              startPosition.set(this.sourceWidth, 0)
              endPosition.set(this.sourceWidth, this.sourceHeight)
            } else {
              startPosition.set(0, this.sourceHeight)
              endPosition.set(this.sourceWidth, this.sourceHeight)
            }
            break
        }

        let edgeworkSegments = this.transformSegmentList([{
          type: SegmentTypes.line,
          startPosition: startPosition,
          endPosition: endPosition,
        }])

        return {
          colour: edgeworkData.colour,
          segments: edgeworkSegments
        }
      })
    }
  }


  /**
   * Generate the list of cutouts for the panel allocation transformed into sheet position/rotation/flip
   * Returns an array of objects, each containing a cutout & data
   */
  generateTransformCutouts() {
    if(!this.orderItem)
      return []

    if(this.orderItem.isEditorGeneratedItem) {

      let cutoutList = this.orderItem.shapeData.cutouts || []
      return _.compact(cutoutList.map(cutout => {
        let cutoutPath = this.transformSegmentList(cutout.path)
        var bounds = new THREE.Box2()
        cutoutPath.forEach(cutoutSeg => {
          bounds = bounds.union(cutoutSeg.calculateBounds())
        })

        return {
          type: cutout.type,
          bounds: bounds,
          path: cutoutPath
        }
      }))
    }

    return []
  }


  /**
   * Transform a segment list from the shape data into the sheet position/rotation/flip
   * Returns an array of GlassSegment objects
   */
  transformSegmentList(segmentList) {
    try {
      let offset = new THREE.Vector2(-this.width/2, -this.height/2)
      if(this.angle != 0 && this.angle != 180)
        offset.set(-this.height/2, -this.width/2)

      let trimOffset = new THREE.Vector2(this.trimLeft, this.trimTop)

      let mid = new THREE.Vector2(this.x + this.width/2, this.y + this.height/2)
      let allocOrigin = new THREE.Vector2(0, 0)
      let rads = MathsUtils.degreesToRadians(this.angle)
      let scale = new THREE.Vector2(this.flippedHorz ? -1 : 1, this.flippedVert ? -1 : 1)

      return segmentList.map(segment => {
        let startPos = new THREE.Vector2(segment.startPosition.x, segment.startPosition.y)
        let endPos = new THREE.Vector2(segment.endPosition.x, segment.endPosition.y)

        startPos.add(trimOffset)
        endPos.add(trimOffset)
        startPos.add(offset)
        endPos.add(offset)

        startPos = MathsUtils.rotate2DPoint(allocOrigin, startPos, rads)
        endPos = MathsUtils.rotate2DPoint(allocOrigin, endPos, rads)

        startPos.multiply(scale)
        startPos.add(mid)
        endPos.multiply(scale)
        endPos.add(mid)

        var segmentData = {
          type: segment.type,
          startPosition: startPos,
          endPosition: endPos,
          startCornerRadius: segment.startCornerRadius,
          endCornerRadius: segment.endCornerRadius
        }

        if(segment.type == SegmentTypes.arc) {
          let arcCenterPos = new THREE.Vector2(segment.arcCenterPosition.x, segment.arcCenterPosition.y)
          arcCenterPos.add(trimOffset)
          arcCenterPos.add(offset)
          arcCenterPos = MathsUtils.rotate2DPoint(allocOrigin, arcCenterPos, rads)
          arcCenterPos.multiply(scale)
          arcCenterPos.add(mid)

          segmentData.arcRadius = segment.arcRadius
          segmentData.arcClockwise = segment.arcClockwise
          segmentData.arcCenterPosition = arcCenterPos

          segmentData.arcStartAngle = MathsUtils.calculateArcPointAngle(segmentData.arcCenterPosition, segmentData.startPosition)
          segmentData.arcEndAngle = MathsUtils.calculateArcPointAngle(segmentData.arcCenterPosition, segmentData.endPosition)

          if(this.flippedHorz)
            segmentData.arcClockwise = !segmentData.arcClockwise
          if(this.flippedVert)
            segmentData.arcClockwise = !segmentData.arcClockwise
        }

        var outputSegment = new GlassSegment(segmentData.type, segmentData)

        // Copy over additional data (used by edgework / cutting lines)
        outputSegment.hasEdgework = segment.hasEdgework
        outputSegment.isCuttingSegment = segment.isCuttingSegment
        outputSegment.isBoundarySegment = segment.isBoundarySegment
        outputSegment.boundaryIndex = segment.boundaryIndex
        outputSegment.orderEditorEdgeworkConfigId = segment.orderEditorEdgeworkConfigId
        outputSegment.edgeworkColour = segment.edgeworkColour
        outputSegment.edgeworkAllowance = segment.edgeworkAllowance
        outputSegment.edgeworkValue = segment.edgeworkValue

        return outputSegment
      })
    } catch(err) {
      console.error(`[OPTI] Error processing segment list in allocation: ${err}`)
      return []
    }
  }


  /**
   * Transform a point in the allocation back to the allocation's local transform before rotation/flipping
   */
  transformPointToLocal(point) {
    let offset = new THREE.Vector2(-this.width/2, -this.height/2)
    if(this.angle != 0 && this.angle != 180)
      offset.set(-this.height/2, -this.width/2)

    let allocOrigin = new THREE.Vector2(0, 0)
    let rads = MathsUtils.degreesToRadians(this.angle)
    let scale = new THREE.Vector2(this.flippedHorz ? -1 : 1, this.flippedVert ? -1 : 1)

    let outputPoint = new THREE.Vector2(point.x, point.y)

    outputPoint.sub(new THREE.Vector2(this.width/2, this.height/2))
    outputPoint.multiply(scale)
    outputPoint = MathsUtils.rotate2DPoint(allocOrigin, outputPoint, -rads)
    outputPoint.sub(offset)
    outputPoint.sub(new THREE.Vector2(this.trimLeft, this.trimTop))

    return outputPoint
  }
}